import { gql } from '@apollo/client'
import { useLazyQuery, useQuery } from '@apollo/client/react'
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import type { A2RecentApplications, A2SearchApplications } from 'types/graphql'

import { useMyTrainingStatus } from 'src/components/TrainingGate/useMyTrainingStatus'
import { TwoDotsStatusIcon } from 'src/components/shared/TwoDotsStatusIcon'
import {
  Alert,
  ArrowRightIcon,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Icon,
  IconButton,
  Link,
  NavigationGroup,
  Spinner,
  Text,
  TextField,
  Tooltip,
  WorkspaceNavigation,
  type DataTableColumn,
} from 'src/design-system/ui'
import { useRecentLeasingApplications } from 'src/hooks/useRecentLeasingApplications'
import { navigate, useLocation, useParams } from 'src/platform/router'
import { A2SearchContext, searchPlaceholder } from './A2SearchContext'
import './A2Workspace.css'

export const RECENT_QUERY = gql`
  query A2RecentApplications(
    $applicationIds: [Int!]!
    $includeListDetails: Boolean!
  ) {
    applications: recentlyViewedLeasingApplications(
      applicationIds: $applicationIds
      includeListDetails: $includeListDetails
      includeStatus: true
    ) {
      id
      primaryApplicantName
      primaryProspectCode
      propertyName
      unitName
      applied @include(if: $includeListDetails)
      lastUpdated @include(if: $includeListDetails)
      twoDotsStatus
      pmsStatus @include(if: $includeListDetails)
      propertyCode @include(if: $includeListDetails)
      primaryEmail @include(if: $includeListDetails)
    }
  }
`
export const SEARCH_QUERY = gql`
  query A2SearchApplications($searchTerm: String!) {
    leasingApplicationSearch(searchTerm: $searchTerm) {
      applications {
        id
        primaryApplicantName
        primaryProspectCode
        propertyName
        unitName
      }
      hasMore
    }
  }
`
const ApplicationLookup = ({
  onOpen,
  applicationLink,
}: {
  onOpen: (id: number) => void
  applicationLink: (
    row: A2SearchApplications['leasingApplicationSearch']['applications'][number],
    onNavigate: (url: string) => void
  ) => ReactNode
}) => {
  const { pms } = useContext(A2SearchContext)
  const [draft, setDraft] = useState('')
  const [matches, setMatches] = useState<
    A2SearchApplications['leasingApplicationSearch'] | null
  >(null)
  const [failed, setFailed] = useState(false)
  const [pendingApplicationId, setPendingApplicationId] = useState<
    number | null
  >(null)
  const request = useRef(0)
  const [lookup, { loading }] = useLazyQuery<A2SearchApplications>(
    SEARCH_QUERY,
    {
      fetchPolicy: 'network-only',
    }
  )
  useEffect(
    () => () => {
      request.current += 1
    },
    []
  )
  // Close the chooser and restore its focus before closing a containing
  // mobile Cases drawer for navigation.
  useEffect(() => {
    if (pendingApplicationId === null) return
    const frame = requestAnimationFrame(() => {
      setPendingApplicationId(null)
      onOpen(pendingApplicationId)
    })
    return () => cancelAnimationFrame(frame)
  }, [pendingApplicationId, onOpen])
  const submit = async () => {
    const code = draft.trim()
    if (!code || loading) return
    const currentRequest = ++request.current
    setMatches(null)
    setFailed(false)
    try {
      const response = await lookup({ variables: { searchTerm: code } })
      if (currentRequest !== request.current) return
      const result = response.data?.leasingApplicationSearch
      if (!result) throw new Error('Application lookup unavailable')
      if (result.applications.length === 1 && !result.hasMore) {
        setDraft('')
        onOpen(result.applications[0].id)
      } else {
        setMatches(result)
      }
    } catch {
      if (currentRequest === request.current) setFailed(true)
    }
  }
  return (
    <>
      <form
        aria-label="Navigate to application"
        className="a2-case-search"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <TextField
          label="Search applications"
          className="ds:min-w-0 ds:flex-1"
          value={draft}
          onChange={(value) => {
            request.current += 1
            setDraft(value)
            setMatches(null)
            setFailed(false)
          }}
          inputProps={{
            maxLength: 100,
            placeholder: searchPlaceholder(pms),
          }}
          errorMessage={
            failed
              ? 'Application lookup is unavailable. Please try again.'
              : matches?.applications.length === 0
                ? 'No accessible application matches your search.'
                : undefined
          }
          trailingAction={
            <Tooltip content="Open application">
              <IconButton
                type="submit"
                size="compact"
                aria-label="Open application"
                isDisabled={!draft.trim() || loading}
              >
                {loading ? (
                  <Spinner size="xs" aria-label="Finding application" />
                ) : (
                  <Icon icon={ArrowRightIcon} size="sm" tone="muted" />
                )}
              </IconButton>
            </Tooltip>
          }
        />
      </form>
      <Dialog
        open={Boolean(matches?.applications.length)}
        onOpenChange={(open) => {
          if (!open) setMatches(null)
        }}
        width="small"
      >
        <DialogTitle>Choose application</DialogTitle>
        <DialogBody>
          <div className="ds:grid ds:gap-3">
            <Text variant="caption" tone="muted">
              More than one application matches. Which would you like to open?
            </Text>
            <ul className="a2-case-list" aria-label="Matching applications">
              {matches?.applications.map((row) =>
                applicationLink(row, () => {
                  setMatches(null)
                  setDraft('')
                  setPendingApplicationId(row.id)
                })
              )}
            </ul>
            {matches?.hasMore && (
              <Text variant="caption" tone="muted">
                Showing the latest {matches.applications.length} matches.
              </Text>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="quiet" onPress={() => setMatches(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export const A2ApplicationsBrowser = ({
  headerAction,
  afterNavigate,
  scrollTop: savedScrollTop,
  presentation = 'sidebar',
}: {
  headerAction?: ReactNode
  afterNavigate?: () => void
  scrollTop?: RefObject<number>
  presentation?: 'sidebar' | 'page'
}) => {
  const localScrollTop = useRef(0)
  const scrollTop = savedScrollTop ?? localScrollTop
  const isPage = presentation === 'page'
  const Container = isPage ? 'section' : 'nav'
  const applicationId = Number(useParams().applicationId)
  const { search } = useLocation()
  const recent = useRecentLeasingApplications()
  const scrollRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLAnchorElement>(null)
  const previousApplicationId = useRef(applicationId)
  const viewed = useQuery<A2RecentApplications>(RECENT_QUERY, {
    variables: {
      applicationIds: recent.map((row) => row.id),
      includeListDetails: isPage,
    },
    skip: !recent.length,
    fetchPolicy: 'cache-and-network',
  })
  const viewedApplications =
    viewed.data?.applications ?? viewed.previousData?.applications
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollTop.current
  }, [scrollTop])
  useEffect(() => {
    if (
      previousApplicationId.current !== applicationId &&
      selectedRef.current
    ) {
      selectedRef.current.scrollIntoView?.({ block: 'nearest' })
      previousApplicationId.current = applicationId
    }
  }, [applicationId, viewedApplications])
  const href = (id?: number) => {
    const params = new URLSearchParams()
    const businessUser = new URLSearchParams(search).get('businessUser')
    if (businessUser) params.set('businessUser', businessUser)
    return `/a2${id === undefined ? '' : `/${id}`}${params.size ? `?${params}` : ''}`
  }
  const open = (url: string) => {
    afterNavigate?.()
    navigate(url)
  }
  const applicationLink = (
    row: A2SearchApplications['leasingApplicationSearch']['applications'][number] & {
      twoDotsStatus?: string | null
    },
    inRecent = false,
    onNavigate = open
  ) => (
    <li key={row.id}>
      <Link
        variant={inRecent ? 'navigation' : 'surface'}
        href={href(row.id)}
        onNavigate={onNavigate}
        aria-current={applicationId === row.id ? 'page' : undefined}
        ref={inRecent && applicationId === row.id ? selectedRef : undefined}
        className="a2-case-link"
      >
        <Text variant="body-strong" as="span">
          {row.primaryApplicantName || `Application #${row.id}`}
        </Text>
        {inRecent && row.twoDotsStatus && (
          <span className="ds:flex ds:items-center ds:gap-1">
            <TwoDotsStatusIcon status={row.twoDotsStatus} size="xs" />
            <Text variant="caption" as="span">
              {row.twoDotsStatus}
            </Text>
          </span>
        )}
        <Text
          variant="caption"
          tone={row.primaryProspectCode ? 'default' : 'muted'}
          as="span"
        >
          {row.primaryProspectCode || 'No prospect code'}
        </Text>
        <Text variant="caption" tone="muted" as="span">
          {[row.propertyName, row.unitName && `Unit ${row.unitName}`]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        {(!inRecent || isPage) && (
          <Text variant="caption" tone="muted" as="span">
            Application #{row.id}
          </Text>
        )}
      </Link>
    </li>
  )
  type RecentRow = A2RecentApplications['applications'][number]
  const formatDate = (value?: string | null, withTime = false) =>
    value
      ? new Date(value).toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          ...(!withTime ? { timeZone: 'UTC' } : {}),
          ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
        })
      : '—'
  const columns: DataTableColumn<RecentRow>[] = [
    {
      id: 'id',
      label: 'Application',
      minWidth: 110,
      sortValue: (row) => row.id,
      render: (row) => (
        <Link href={href(row.id)} onNavigate={open}>
          #{row.id}
        </Link>
      ),
    },
    {
      id: 'applied',
      label: 'Applied',
      minWidth: 110,
      sortValue: (row) => row.applied ?? null,
      render: (row) => formatDate(row.applied),
    },
    {
      id: 'lastUpdated',
      label: 'Last Updated',
      minWidth: 155,
      sortValue: (row) => row.lastUpdated ?? null,
      render: (row) => formatDate(row.lastUpdated, true),
    },
    {
      id: 'twoDotsStatus',
      label: 'Two Dots',
      minWidth: 150,
      sortValue: (row) => row.twoDotsStatus ?? null,
      render: (row) => row.twoDotsStatus || '—',
    },
    {
      id: 'pmsStatus',
      label: 'PMS',
      minWidth: 100,
      sortValue: (row) => row.pmsStatus ?? null,
      render: (row) => row.pmsStatus || '—',
    },
    {
      id: 'propertyCode',
      label: 'Prop. Code',
      minWidth: 110,
      sortValue: (row) => row.propertyCode ?? null,
      render: (row) => row.propertyCode || '—',
    },
    {
      id: 'property',
      label: 'Property',
      minWidth: 150,
      sortValue: (row) => row.propertyName,
      render: (row) => row.propertyName || '—',
    },
    {
      id: 'unit',
      label: 'Unit',
      minWidth: 100,
      sortValue: (row) => row.unitName,
      render: (row) => row.unitName || '—',
    },
    {
      id: 'prospect',
      label: 'pCode',
      minWidth: 120,
      sortValue: (row) => row.primaryProspectCode ?? null,
      render: (row) => row.primaryProspectCode || '—',
    },
    {
      id: 'primary',
      label: 'Primary',
      minWidth: 150,
      sortValue: (row) => row.primaryApplicantName ?? null,
      render: (row) => row.primaryApplicantName || '—',
    },
    {
      id: 'email',
      label: 'Primary Email',
      minWidth: 180,
      sortValue: (row) => row.primaryEmail ?? null,
      render: (row) => row.primaryEmail || '—',
    },
  ]
  return (
    <Container
      id={isPage ? undefined : 'a2-case-list'}
      aria-label="Applications"
      className={isPage ? 'a2-applications-page' : 'a2-cases'}
    >
      <header className={isPage ? 'a2-applications-header' : 'a2-cases-header'}>
        {isPage ? (
          <div className="ds:grid ds:gap-2">
            <Text as="h1" variant="page-title">
              Applications
            </Text>
            <Text tone="muted">
              Find an application or pick up where you left off.
            </Text>
          </div>
        ) : (
          <Link variant="navigation" href={href()} onNavigate={open}>
            <Text as="h2" variant="body">
              Applications
            </Text>
          </Link>
        )}
        {headerAction}
      </header>
      <ApplicationLookup
        key={applicationId}
        onOpen={(id) => open(href(id))}
        applicationLink={(row, onNavigate) =>
          applicationLink(row, false, onNavigate)
        }
      />
      <div
        ref={scrollRef}
        className="a2-cases-scroll"
        onScroll={(event) => {
          scrollTop.current = event.currentTarget.scrollTop
        }}
      >
        <NavigationGroup label="Recently viewed">
          {viewed.loading && !viewedApplications ? (
            <Spinner aria-label="Loading recently viewed applications" />
          ) : viewed.error ? (
            <Alert tone="warning">
              Recent applications unavailable.{' '}
              <Button onPress={() => void viewed.refetch()}>Retry</Button>
            </Alert>
          ) : isPage && viewedApplications?.length ? (
            <DataTable
              ariaLabel="Recently viewed applications"
              items={viewedApplications}
              columns={columns}
              getRowId={(row) => row.id}
              allowsSorting
              allowsColumnResizing
            />
          ) : (
            <ul
              className="a2-case-list"
              aria-label="Recently viewed applications"
            >
              {recent.flatMap(({ id }) => {
                const row = viewedApplications?.find((entry) => entry.id === id)
                return row ? [applicationLink(row, true)] : []
              })}
            </ul>
          )}
          {(!recent.length ||
            (!viewed.loading &&
              !viewed.error &&
              !viewedApplications?.length)) && (
            <Text variant="caption" tone="muted">
              Applications you view will appear here.
            </Text>
          )}
        </NavigationGroup>
      </div>
    </Container>
  )
}

export const A2CaseNavigation = ({
  children,
}: {
  children: (toggle: ReactNode) => ReactNode
}) => {
  const scrollTop = useRef(0)
  const training = useMyTrainingStatus()
  if (training.trainingRequired || training.trainingStatusLoading)
    return children(undefined)
  return (
    <WorkspaceNavigation
      label="Application list"
      storageKey="a2CaseListHidden"
      id="a2-case-list"
      renderPanel={(headerAction, afterNavigate) => (
        <A2ApplicationsBrowser
          headerAction={headerAction}
          afterNavigate={afterNavigate}
          scrollTop={scrollTop}
        />
      )}
    >
      {children}
    </WorkspaceNavigation>
  )
}
